import { AuthenticationError } from "../utils/errors.js";

const LABELS = new Set([
  "consent_required",
  "interaction_required",
  "invalid_scope",
  "invalid_resource",
  "invalid_grant",
  "no_tokens_found",
  "token_refresh_required",
  "basic_action",
  "additional_action",
]);
const AAD_CODES = new Set([
  "65001",
  "65002",
  "53003",
  "50076",
  "50079",
  "700016",
  "7000112",
  "50173",
]);

// Remember only constructor-validated categories. Public Error properties can be
// mutated or forged by callers; diagnostic propagation must not trust them.
const validatedReasons = new WeakMap<object, string>();

/** Closed diagnostic projection from an error created by this module; no raw parsing. */
export function getSafeCopilotStudioManagementAuthReason(error: unknown): string {
  return error !== null && typeof error === "object"
    ? (validatedReasons.get(error) ?? "unavailable")
    : "unavailable";
}

function ownDataValue(error: object, key: string): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(error, key);
    return descriptor && "value" in descriptor ? descriptor.value : undefined;
  } catch {
    return undefined;
  }
}

/** Carries one allowlisted category only, never a raw MSAL/CLI error or challenge. */
export class CopilotStudioManagementAuthError extends AuthenticationError {
  readonly reason: string;
  constructor(error?: unknown, identityMismatch = false) {
    let reason = identityMismatch ? "identity_mismatch" : "unavailable";
    if (
      !identityMismatch &&
      error !== null &&
      typeof error === "object" &&
      validatedReasons.has(error)
    )
      reason = getSafeCopilotStudioManagementAuthReason(error);
    else if (!identityMismatch && error !== null && typeof error === "object") {
      for (const value of [ownDataValue(error, "errorCode"), ownDataValue(error, "subError")]) {
        if (typeof value === "string" && value.length <= 64 && LABELS.has(value)) {
          reason = value;
          break;
        }
      }
      // Only the bounded leading diagnostic, not JSON/quoted claims or later lines.
      const message = ownDataValue(error, "message");
      const prefix =
        typeof message === "string" ? (message.slice(0, 1024).split(/[\r\n{"]/u, 1)[0] ?? "") : "";
      const code = /\bAADSTS(\d{5,7}):/.exec(prefix)?.[1];
      if (code && AAD_CODES.has(code)) reason = `AADSTS${code}`;
    }
    super(
      `Copilot Studio management authentication unavailable [${reason}] for the selected account; no sign-in or permission change was attempted.`,
    );
    this.name = "CopilotStudioManagementAuthError";
    this.reason = reason;
    validatedReasons.set(this, reason);
  }
}
